// The service catalogue's own vocabulary (docs/plans/phase1-finance-catalogue.md §2.1).
//
// Kinds and categories are Wodi's grouping of what a facility charges for, not clinical
// or national codes: those travel separately as code + display + system version.

/** Services are performed; stock items are what the stock ledger moves. */
export const SERVICE_ITEM_KINDS = ['service', 'stock_item'] as const;
export type ServiceItemKind = (typeof SERVICE_ITEM_KINDS)[number];

export const SERVICE_ITEM_CATEGORIES = [
  'consultation',
  'procedure',
  'lab_test',
  'imaging',
  'medicine',
  'consumable',
  'bed_day',
  'nursing',
  // The mortuary's daily storage charge (docs/plans/phase2-mortuary-and-notifications.md §2.5).
  'mortuary',
  'other',
] as const;
export type ServiceItemCategory = (typeof SERVICE_ITEM_CATEGORIES)[number];

const STOCK_CATEGORIES: ReadonlySet<ServiceItemCategory> = new Set(['medicine', 'consumable']);

/**
 * Medicines and consumables are stock items; every other category is a service. The
 * database has the same rule as a CHECK constraint.
 */
export function categoryMatchesKind(category: ServiceItemCategory, kind: ServiceItemKind): boolean {
  return STOCK_CATEGORIES.has(category) === (kind === 'stock_item');
}

/** The code systems an item can carry. `legacy` is the facility's own old code. */
export const ITEM_CODE_SYSTEMS = ['sha_intervention', 'knhts', 'loinc', 'atc', 'legacy'] as const;
export type ItemCodeSystem = (typeof ITEM_CODE_SYSTEMS)[number];

/** Where a code came from. National codes are never typed from memory (plan §2.2). */
export const ITEM_CODE_SOURCES = ['terminology_cache', 'import', 'manual'] as const;
export type ItemCodeSource = (typeof ITEM_CODE_SOURCES)[number];

/** Only the facility's own legacy codes may be typed in by hand. */
export function codeSourceAllowed(system: ItemCodeSystem, source: ItemCodeSource): boolean {
  return source !== 'manual' || system === 'legacy';
}
